'use strict';

import React from 'react';
import { useState, useEffect } from 'react';
import { Table, Button, Modal, Row, Col, Form, Popover, OverlayTrigger } from 'react-bootstrap';

import useRequest from '../helper/useRequest';
import { toast } from 'react-toastify';
import Alert from '../helper/Alert';

import useConfirmation from '../helper/Confirmation';

import { customFilterFunction } from '../helper/CustomFilter';
import TableHeader from '../helper/TableHeader';

import Loader from '../helper/Loader';

import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    useReactTable,
} from '@tanstack/react-table';

const columnHelper = createColumnHelper();


function TestimonialModal({ modal, setModal, testimonial, updateTestimonial}) {
    const [message, setMessage] = useState("");

    const [feedback, setFeedback] = useState("");
    const [isEnabled, setIsEnabled] = useState(false);
    const [person, setPerson] = useState("");
    const [func, setFunc] = useState("");

    const closeModal = () => {
        setModal(false);
    }

    const onSubmit = () => {
        if (feedback.trim().length == 0)
            return setMessage("Sie müssen ein Feedback angeben.");

        setMessage("");
        updateTestimonial(testimonial, feedback, isEnabled, person, func);
    }

    useEffect(() => {
        setFeedback("");
        setIsEnabled(false);
        setPerson("");
        setFunc("");

        if (!testimonial) return;

        setFeedback(testimonial.Feedback);
        setIsEnabled(testimonial.IsEnabled);
        setPerson(testimonial.Person);
        setFunc(testimonial.Function);
    }, [testimonial])

    return (
        <Modal size="lg" show={modal} onHide={closeModal}>
            <Modal.Header closeButton>
                <Modal.Title>Testimonial {testimonial && "bearbeiten"}{!testimonial && "hinzufügen"}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {message && <Alert type="error" message={message} />}

                <Row className="mb-2">
                    <Col sm={2}>
                        <Form.Label htmlFor="isEnabled">Ist aktiviert</Form.Label>
                    </Col>
                    <Col sm={10}>
                        <Form.Check aria-label={"Ist aktiviert?"} name="isEnabled" checked={isEnabled} value="1" checked={isEnabled} onChange={ (e) => { return setIsEnabled(e.target.checked); }} />
                    </Col>
                </Row>

                <Row className="mb-2">
                    <Col sm={2}>
                        <Form.Label htmlFor="person">Person</Form.Label>
                    </Col>
                    <Col sm={10}>
                        <Form.Control id="person" value={person} onChange={(e) => { return setPerson(e.target.value); }} size="sm" />
                    </Col>
                </Row>

                <Row className="mb-2">
                    <Col sm={2}>
                        <Form.Label htmlFor="func">Funktion</Form.Label>
                    </Col>
                    <Col sm={10}>
                        <Form.Control id="func" value={func} onChange={(e) => { return setFunc(e.target.value); }} size="sm" />
                    </Col>
                </Row>

                <Row className="mb-2">
                    <Col sm={6}>
                        <Form.Label htmlFor="description">Feedback</Form.Label>
                    </Col>
                    <Col sm={12}>
                        <Form.Control id="description" as="textarea" rows={10} value={feedback} onChange={(e) => setFeedback(e.target.value)} size="sm" />
                    </Col>
                </Row>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={closeModal}>
                    Abbrechen
                </Button>
                <Button variant="primary" onClick={() => onSubmit()}>
                    {testimonial && "Speichern"}{!testimonial && "Hinzufügen"}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

function Testimonials() {
    const { request } = useRequest();
    const context = useConfirmation();

    const [sorting, setSorting] = useState([]);
    const [columnFilters, setColumnFilters] = useState([]);

    const [testimonial, setTestimonial] = useState({});
    const [testimonials, setTestimonials] = useState(false);
    const [modal, setModal] = useState(false);

    const openAdd = () => {
        setTestimonial(null);
        setModal(true);
    };

    const editTestimonial = (testimonial) => {
        setTestimonial(testimonial);
        setModal(true);
    };

    const deleteTestimonial = async (testimonial) => {
        try {
            let data = { TestimonialID: testimonial.TestimonialID };
            let result = await request("/Operator/DeleteTestimonial", data);
            let json = result.data;
            if (json.r == 1) {
                toast.success("Testimonial wurde gelöscht.");
                setTestimonials((items) => {
                    return items.filter((item) => item.TestimonialID != testimonial.TestimonialID);
                });
            }
            else {
                toast.error(json.message);
            }
        }
        catch (ex) {
            toast.error(ex.message);
        }
    }

    const updateTestimonial = async (testimonial, feedback, isEnabled, person, func) => {
        try {
            let testimonialID = 0;
            if (testimonial) {
                testimonialID = testimonial.TestimonialID;
            }
            let data = { TestimonialID: testimonialID, Feedback: feedback, IsEnabled: isEnabled, Person: person, Function: func };
            let result = await request("/Operator/UpdateTestimonial", data);
            let json = result.data;
            if (json.r == 1) {
                
                setTestimonials((items) => {
                    if (!testimonial) {
                        toast.success("Testimonial wurde hinzugefügt.");
                        return [...items, json.testimonial];
                    }
                    
                    toast.success("Testimonial wurde aktualisiert.");
                    return items.map((item) => {
                        if (item.TestimonialID == testimonial.TestimonialID) {
                            item.Feedback = feedback;
                            item.IsEnabled = isEnabled;
                            item.Person = person;
                            item.Function = func;
                            return item;
                        }
                        else {
                            return item;
                        }
                    });
                });
                setModal(false);
            }
            else {
                toast.error(json.message);
            }
        }
        catch (ex) {
            toast.error(ex.message);
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                let result = await request("/Operator/GetTestimonials");
                let json = result.data;
                if (json.r != 1) {
                    if (json.message) {
                        toast.error(json.message);
                    }
                    else {
                        toast.error("Es ist ein Fehler aufgetreten.");
                    }
                }
                else {
                    setTestimonials(json.testimonials);
                }
            }
            catch (ex) {
                console.error(ex);
                toast.error("Es ist ein Fehler aufgetreten.");
            }
        };
        fetchData();
    }, []);

    const columns = [
        columnHelper.accessor('Feedback', {
            header: () => <span>Feedback</span>,
            cell: info => info.getValue(),
            enableColumnFilter: true,
        }),
        columnHelper.accessor('Person', {
            header: () => <span>Person</span>,
            cell: info => info.getValue(),
            enableColumnFilter: true,
        }),
        columnHelper.accessor('Function', {
            header: () => <span>Funktion</span>,
            cell: info => info.getValue(),
            enableColumnFilter: true,
        }),
        columnHelper.accessor('IsEnabled', {
            header: () => <span>Aktiviert</span>,
            cell: info => {
                return <div className="text-center">{info.getValue() ? "Ja" : "Nein"}</div>;
            },
            enableColumnFilter: false,
        }),
        columnHelper.accessor('TestimonialID', {
            size: 200,
            header: () => <span></span>,
            cell: info => (
                <div className="text-end">
                    <Button variant="primary" className="me-2" onClick={() => editTestimonial(info.row.original)}>Bearbeiten</Button>
                    <Button variant="danger" onClick={() => context.openModal("Testimonial wirklich löschen?", "Einmal gelöscht, kann dieses Testimonial nicht wiederhergestellt werden.", () => deleteTestimonial(info.row.original))}>Löschen</Button>
                </div>
            ),
            enableSorting: false,
            enableColumnFilter: false,
        }) 
            
    ];

    const reactTable = useReactTable({
        data: testimonials || [],
        columns,
        state: {
            sorting,
            columnFilters
        },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,

        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    if (testimonials === false) return <Loader />

    return (
        <>
            <Button onClick={() => openAdd() }>Testimonial hinzufügen</Button>
            <Table striped>
                <thead>
                    {reactTable.getHeaderGroups().map(headerGroup => (
                        <TableHeader key={headerGroup.id} table={reactTable} headerGroup={headerGroup} flexRender={flexRender} />
                    ))}
                </thead>
                <tbody>
                    {reactTable.getRowModel().rows.map(row => (
                        <tr key={row.id}>
                            {row.getVisibleCells().map(cell => {
                                return (
                                    <td key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                )
                            })}
                        </tr>
                    ))}
                </tbody>
            </Table>

            <TestimonialModal modal={modal} setModal={setModal} testimonial={testimonial} updateTestimonial={updateTestimonial} />
        </>
    );
}

export default Testimonials;